<template>
    <div>
        <div class="mx-auto p-4">
            <div class="flex justify-center mt-16">
                <img
                    class="mascot"
                    :src="`/images/mascots/${errorCode == '503' ? 'teamwork' : 'cry'}.png`"
                />
            </div>

            <div class="flex justify-center text-center">
                <h1 :class="[screenSize !== 'small' ? 'mt-10 font-display-2' : 'mt-6 font-display-3']">
                    {{ errorCode }} {{ errorCode && screenSize !== 'small' ? ' - ' : '' }}
                    <br v-if="screenSize === 'small'" />
                    {{ $t([`errors.errorPages.${errorCode}.h1`, 'errors.errorPages.defaultErrorCode.h1']) }}
                </h1>
            </div>

            <div class="flex justify-center mt-4 text-slate-600">
                <div class="mx-auto max-w-md text-center">
                    <template v-if="errorCode === '503'">
                        <p class="mb-4">{{ $t('errors.errorPages.503.text1') }}</p>
                        <p
                            v-if="estimatedMaintenanceCompletionTime"
                            class="mb-8"
                        >
                            {{
                                $t('errors.errorPages.503.text2WithEstimatedCompletion', {
                                    time: estimatedMaintenanceCompletionTime,
                                })
                            }}
                        </p>
                        <p
                            v-else
                            class="mb-8"
                        >
                            {{ $t('errors.errorPages.503.text2') }}
                        </p>
                        <p class="font-body-2-medium mb-8">{{ $t('errors.errorPages.503.text3') }}</p>
                    </template>
                    <span v-else>
                        {{ $t([`errors.errorPages.${errorCode}.text`, 'errors.errorPages.defaultErrorCode.text']) }}
                    </span>
                </div>
            </div>

            <div
                v-if="errorCode !== '503'"
                class="flex flex-col md:flex-row justify-center mt-8 mb-2"
            >
                <!-- native modifier has been removed, please confirm whether the function has been affected  -->
                <BtnCta
                    v-if="errorCode === '404'"
                    variant="secondary"
                    :label="$t('errors.errorPages.button_1')"
                    :loading="isSending"
                    @click="sendReport"
                />
                <Btn
                    v-if="!route().current('home.index')"
                    :label="$t('errors.errorPages.button_2')"
                    color="primary"
                    class="mt-6 md:ml-6 md:mt-0"
                    href="/"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import route from '@router/route';
import { mapGetters } from 'vuex';
import { report404Page } from '@/api/backend/report';
import { keepUrlGuard } from '@/router/guards';
import { useEstimatedMaintenanceCompletionTime } from '@/composables/systemStatus';

/**
 * Note: In case you would ever want to navigate to another error page from within the error page,
 * you need to add `force: true` to your router push/replace statement.
 */
export default {
    name: 'ErrorIndex',
    beforeRouteUpdate: keepUrlGuard,
    beforeRouteEnter: keepUrlGuard,
    props: {
        /**
         * Status code for which to render an error.
         * When navigating to any error page, you need to provide the value via the router state as `state: { errorCode: }`
         */
        errorCode: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            isSending: false,
        };
    },
    computed: {
        ...mapGetters({
            screenSize: 'ui/screenSize',
        }),
        estimatedMaintenanceCompletionTime() {
            return useEstimatedMaintenanceCompletionTime();
        },
    },
    created() {
        this.$store.dispatch('ui/disableFooter');
        this.$store.dispatch('ui/disableSignButtons');
    },
    methods: {
        route,
        sendReport() {
            if (this.isSending) {
                return;
            }

            if (this.errorCode === '404') {
                this.isSending = true;
                report404Page(window.location.href)
                    .then(() => {
                        this.$store.dispatch('flashMessages/showSuccess', {
                            content: this.$t('errors.errorPages.flash_message'),
                            progress: { showProgress: false },
                        });
                    })
                    .finally(() => {
                        this.isSending = false;
                    });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.height-48 {
    height: 48px;
}

.mascot {
    height: 160px;
}

@screen md {
    .mascot {
        height: 237px;
    }
}
</style>
